import axios from 'axios';

export const BASE_URL = 'https://tapi.speechsensepro.com';

export const adminApi = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Note: setAdminAuthToken is kept for backward compatibility but doesn't store tokens
// Authentication is handled via HTTP-only cookies set by the backend
export const setAdminAuthToken = (token) => {
  // No-op: Authentication uses HTTP-only cookies, not localStorage tokens
  // This function is kept to avoid breaking existing code that calls it
};

// Authentication is handled via HTTP-only cookies (adminToken cookie)
// The withCredentials: true setting automatically sends cookies with requests
// No need to manually add Authorization headers
adminApi.interceptors.request.use((config) => {
  // Cookies are automatically included with withCredentials: true
  return config;
});

const isAuthRoute = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  const pathname = window.location?.pathname || '';
  return pathname.startsWith('/admin/login') || pathname.startsWith('/admin/verify');
};

const handleApiError = (error) => {
  if (error.response?.status === 401) {
    const errorCode = error.response?.data?.code;

    if (errorCode === 'NO_UNVERIFIED_TOKEN' || errorCode === 'UNVERIFIED_TOKEN_INVALID') {
      // Redirect to login without alert
      console.warn('Authentication error:', errorCode);
      window.location.href = '/admin/login';
      return;
    }

    if (errorCode === 'OTP_INVALID' || errorCode === 'OTP_EXPIRED') {
      // Show OTP error, allow retry - don't redirect
      return;
    }

    if (errorCode === 'TOKEN_INVALID' || errorCode === 'NO_TOKEN') {
      // Session expired, redirect to login
      if (!isAuthRoute()) {
        console.warn('Session expired:', errorCode);
        window.location.href = '/admin/login';
      }
      return;
    }

    if (errorCode === 'ADMIN_NOT_FOUND') {
      window.alert('Admin account not found. Please contact support.');
      return;
    }

    if (errorCode === 'SESSION_NOT_FOUND') {
      window.alert('Session not found. Please login again.');
      return;
    }

    if (errorCode === 'ACCESS_DENIED') {
      window.alert('Access denied. Admin privileges required.');
      return;
    }

    // Default case for other 401 errors
    if (!isAuthRoute()) {
      window.location.href = '/admin/login';
    }
  } else if (error.response?.status === 403) {
    window.alert('Access denied. Admin privileges required.');
  } else if (error.response?.status === 400 || error.response?.status === 404) {
    // Let callers display response.data.error (e.g. validation, not found)
  } else {
    window.alert('An error occurred. Please try again.');
  }
};

adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    handleApiError(error);
    return Promise.reject(error);
  }
);

export const checkAdminAuth = async () => {
  try {
    await adminApi.get('/admin/auth/check');
    return true;
  } catch (error) {
    console.log('Auth check failed:', {
      status: error.response?.status,
      code: error.response?.data?.code,
      message: error.response?.data?.error
    });
    if (error.response?.status === 401) {
      return false;
    }
    throw error;
  }
};

export const loginAdmin = (email, turnstileToken) => {
  console.log('Attempting login for:', email);
  return adminApi.post('/admin/login', {
    email,
    ...(turnstileToken ? { turnstile_token: turnstileToken } : {}),
  });
};

export const verifyAdminOtp = (otp) => {
  console.log('Verifying OTP:', otp.substring(0, 2) + '****');
  return adminApi.post('/admin/verify', {
    otp
  });
};

export const logoutAdmin = () => adminApi.post('/admin/logout');

export const fetchPendingUsers = () => adminApi.get('/admin/users');

export const fetchAllUsers = (params) => adminApi.get('/admin/users/all', { params });

export const fetchUsersByStatus = (params) => adminApi.get('/admin/users', { params });

export const fetchPolicies = (params) => adminApi.get('/api/policies', { params });

export const fetchPolicyById = (id) => adminApi.get(`/api/policies/${id}`);

export const createPolicy = (policyData) => adminApi.post('/api/policies/admin', policyData);

export const updatePolicy = (id, policyData) => adminApi.put(`/api/policies/admin/${id}`, policyData);

export const fetchAdminStats = () => adminApi.get("/admin/stats");

export const fetchSubscriptions = (params) =>
  adminApi.get('/admin/subscriptions', { params });

export const fetchTransactions = (params) =>
  adminApi.get('/admin/transactions', { params });

export const downloadSubscriptions = (params) =>
  adminApi.get('/admin/subscriptions', { params, responseType: 'blob' });

export const downloadTransactions = (params) =>
  adminApi.get('/admin/transactions', { params, responseType: 'blob' });

export const fetchStripePromotionCodes = (params) =>
  adminApi.get('/admin/stripe/promotion-codes', { params });

export const createStripePromotionCode = (body) =>
  adminApi.post('/admin/stripe/promotion-codes', body);

export const deactivateStripePromotionCode = (body) =>
  adminApi.post('/admin/stripe/promotion-codes/deactivate', body);

export const approveUser = (userId) => adminApi.post(`/admin/users/${userId}/approve`);

export const rejectUser = (userId, data) => {
  console.log('rejectUser called with:', { userId, data });
  if (data && Object.keys(data).length > 0) {
    console.log('Sending rejection with data:', data);
    return adminApi.post(`/admin/users/${userId}/reject`, data);
  }
  console.log('Sending rejection without data');
  return adminApi.post(`/admin/users/${userId}/reject`);
};

// How-to Tutorials API
export const fetchTutorials = (params) => adminApi.get('/api/howts', { params });

export const fetchTutorialById = (uuid) => adminApi.get(`/api/howts/${uuid}`);

export const createTutorial = (formData) => {
  return adminApi.post('/api/howts', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const updateTutorial = (uuid, formData) => {
  return adminApi.put(`/api/howts/${uuid}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const toggleTutorialPublish = (uuid, isPublished) => {
  return adminApi.put(`/api/howts/${uuid}/publish`, { isPublished });
};

export const deleteTutorial = (uuid) => adminApi.delete(`/api/howts/${uuid}`);

// Usage Reporting API
export const fetchUsageReportSummary = (params) => 
  adminApi.get('/admin/usage-report/summary', { params });

export const fetchUsageReport = (params) => 
  adminApi.get('/admin/usage-report', { params });

export const fetchUsageReportDetailed = (params) => 
  adminApi.get('/admin/usage-report/detailed', { params });

export const fetchUserLoginHistory = (userId, params) =>
  adminApi.get(`/admin/usage-report/user/${userId}/logins`, { params });

export const fetchUserSessions = (userId) =>
  adminApi.get(`/admin/users/${userId}/sessions`);

export const fetchDaysGrant = (profileUuid) =>
  adminApi.get(`/api/auth/profiles/${profileUuid}/days-grant`);

export const updateDaysGrant = ({ profileUuid, days_grant }) =>
  adminApi.put('/api/auth/profiles/days-grant', { profileUuid, days_grant });

export const fetchProfileHistory = (profileUuid) =>
  adminApi.get(`/api/auth/profiles/${profileUuid}/history`);

// Organizations API
export const fetchOrganizations = (params) => adminApi.get('/api/organizations', { params });

export const fetchOrganizationById = (uuid) => adminApi.get(`/api/organizations/${uuid}`);

export const createOrganization = (formData) =>
  adminApi.post('/api/organizations', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

export const updateOrganization = (uuid, data) => adminApi.put(`/api/organizations/${uuid}`, data);

// Contracts
export const fetchOrgContracts = (id) =>
  adminApi.get(`/api/organizations/${id}/contracts`);

export const createContract = (id, formData) =>
  adminApi.post(`/api/organizations/${id}/contracts`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

// Members
export const addOrgSlp = (id, data) => adminApi.post(`/api/organizations/${id}/slps`, data);

export const addOrgAdmin = (id, data) =>
  adminApi.post(`/api/organizations/${id}/org-admins`, data);

// Plans for license selection
export const fetchSubscriptionPlans = () =>
  adminApi.get('/api/plans', { params: { kind: 'subscription' } });

export const fetchBillingPlans = () => adminApi.get('/api/plans');

export const fetchAdminPlanByUuid = (uuid) => adminApi.get(`/admin/plans/${uuid}`);

export const updatePlanByUuid = (uuid, body) => adminApi.put(`/api/plans/${uuid}`, body);

export const createCheckoutSetupSession = (userUuid, payload) =>
  adminApi.post(`/admin/users/${userUuid}/stripe/checkout-setup-session`, payload);

export const disableUser = (userId) => adminApi.post(`/admin/users/${userId}/disable`);

export const enableUser = (userId) => adminApi.post(`/admin/users/${userId}/enable`);

